/**
 * Canton Tea — PDP Variant Gallery Switcher
 * Theme: Prestige (Archetype Themes v6.x)
 * Compatible with: Recharge Subscriptions
 *
 * HOW IT WORKS:
 * - On page load: detects where the customer came from (collection page) and
 *   auto-selects the matching variant + gallery (e.g. Loose Leaf or Tea Bags)
 * - On variant change: filters the gallery to show images matching the selection
 * - Source detection order: ?ct_src= URL param → document.referrer → default
 *
 * SETUP REQUIRED:
 * 1. Tag images in Shopify admin with alt text containing the keyword (see CONFIG below)
 * 2. Add this file to your theme assets and include it in theme.liquid (see SOP)
 * 3. Add collection-link-src.liquid to your product-card snippet (see SOP)
 *    so collection pages append ?ct_src= to product links automatically
 */

(function () {
  'use strict';

  // ─── CONFIGURATION ──────────────────────────────────────────────────────────
  // optionValues: keys must exactly match the variant option values in Shopify admin
  // altTextKeyword: must appear somewhere in the image alt text in Shopify admin
  const CONFIG = {
    optionValues: {
      'Loose Leaf':  'loose-leaf',   // images tagged: "Product Name loose-leaf"
      'Pyramid':     'pyramid',      // images tagged: "Product Name pyramid"
      'Tea Bags':    'pyramid',      // alias — maps to same gallery set
    },
    // Source routing: collection handles (or referrer URL fragments) that map to a gallery keyword.
    // Add all collection handles that should pre-select a variant type.
    sourceMap: {
      'loose-leaf': 'loose-leaf',    // collection handle contains 'loose-leaf' → show Loose Leaf gallery
      'loose_leaf': 'loose-leaf',
      'looseleaf':  'loose-leaf',
      'tea-bags':   'pyramid',       // collection handle contains 'tea-bags' → show Pyramid gallery
      'tea_bags':   'pyramid',
      'pyramid':    'pyramid',
      'teabags':    'pyramid',
    },
    showAllFallback: true,           // show all images if no keyword matches found
    initDelay: 400,                  // ms — allow Recharge widget to render before init
  };
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Extract the format keyword from a variant object.
   * Checks option1, option2, option3 against CONFIG.optionValues.
   */
  function getKeyword(variant) {
    if (!variant) return null;
    for (const opt of ['option1', 'option2', 'option3']) {
      const val = variant[opt];
      if (val && CONFIG.optionValues[val]) {
        return CONFIG.optionValues[val];
      }
    }
    return null;
  }

  /**
   * Filter the Prestige gallery by keyword.
   * Matches against img alt text (lowercase, trimmed).
   */
  function filterGallery(keyword) {
    // Prestige v6 gallery selectors — covers both old and new markup
    const gallery =
      document.querySelector('product-media-gallery') ||
      document.querySelector('.product-gallery') ||
      document.querySelector('[data-product-gallery]');

    if (!gallery) return;

    const items = gallery.querySelectorAll(
      'li[data-media-id], .product-gallery__item, .media-gallery__item, [data-media-id]'
    );

    let matchCount = 0;

    items.forEach(function (item) {
      const img = item.querySelector('img, [data-src]');
      if (!img) return;
      const alt = (img.getAttribute('alt') || img.getAttribute('data-alt') || '').toLowerCase().trim();
      const matches = alt.includes(keyword.toLowerCase());
      if (matches) matchCount++;

      item.style.display = matches ? '' : 'none';
    });

    // Fallback: if no images are tagged, show everything
    if (matchCount === 0 && CONFIG.showAllFallback) {
      items.forEach(function (item) { item.style.display = ''; });
    }
  }

  /**
   * Get product JSON from the page (Shopify injects this as a script tag).
   */
  function getProductJSON() {
    const el =
      document.querySelector('script[type="application/json"][id*="product-json"]') ||
      document.querySelector('[data-product-json]') ||
      document.querySelector('#ProductJson-product-template');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /**
   * Find a variant by ID from the product JSON.
   */
  function findVariant(variantId) {
    const product = getProductJSON();
    if (!product) return null;
    return product.variants.find(function (v) { return v.id === parseInt(variantId, 10); }) || null;
  }

  /**
   * Main handler — called on any variant change.
   */
  function handleVariantChange(variant) {
    const keyword = getKeyword(variant);
    if (keyword) filterGallery(keyword);
  }

  // ── Source routing ─────────────────────────────────────────────────────────

  /**
   * Determine which gallery to show on initial page load based on where the
   * customer came from.
   *
   * Priority:
   *  1. ?ct_src= URL param   — set by collection-link-src.liquid on product card links
   *  2. document.referrer    — fallback when the param is absent (direct nav, etc.)
   *
   * Returns a CONFIG.optionValues keyword (e.g. 'loose-leaf' or 'pyramid'), or null.
   */
  function getSourceKeyword() {
    // 1. URL param: ?ct_src=loose-leaf  or  ?ct_src=tea-bags
    try {
      const param = new URLSearchParams(window.location.search).get('ct_src');
      if (param) {
        const kw = CONFIG.sourceMap[param.toLowerCase()];
        if (kw) return kw;
      }
    } catch (e) { /* URLSearchParams not supported — fall through */ }

    // 2. Referrer: parse the URL path of the referring page
    const ref = (document.referrer || '').toLowerCase();
    if (ref) {
      for (const fragment in CONFIG.sourceMap) {
        if (ref.indexOf(fragment) !== -1) {
          return CONFIG.sourceMap[fragment];
        }
      }
    }

    return null;
  }

  /**
   * Find the first option value label (e.g. "Loose Leaf") that maps to the
   * given gallery keyword, then programmatically select it in the theme UI.
   *
   * Tries (in order):
   *  a) Prestige radio input  — input[type="radio"][value="Loose Leaf"]
   *  b) Prestige swatch/button — [data-option-value="Loose Leaf"], [data-value="Loose Leaf"]
   *  c) Shopify select#id     — find variant id from product JSON, set & dispatch change
   *  d) Direct gallery filter  — last resort if no selector found
   */
  function autoSelectVariantFromSource() {
    const keyword = getSourceKeyword();
    if (!keyword) return;

    // Resolve keyword → option label (e.g. 'loose-leaf' → 'Loose Leaf')
    const optionLabel = Object.keys(CONFIG.optionValues).find(
      function (k) { return CONFIG.optionValues[k] === keyword; }
    );
    if (!optionLabel) { filterGallery(keyword); return; }

    // a) Radio input (most common in Prestige v6)
    const radio = document.querySelector(
      'input[type="radio"][value="' + optionLabel + '"]'
    );
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      // Prestige reads click on the label, not just change on the input
      const lbl = radio.closest('label') || document.querySelector('label[for="' + radio.id + '"]');
      if (lbl) lbl.click();
      return;
    }

    // b) Swatch / button element
    const btn = document.querySelector(
      '[data-option-value="' + optionLabel + '"], [data-value="' + optionLabel + '"]'
    );
    if (btn) { btn.click(); return; }

    // c) Select[name="id"] — set variant id and fire events
    const product = getProductJSON();
    const select  = document.querySelector('select[name="id"], select[data-variant-select]');
    if (product && select) {
      const match = product.variants.find(function (v) {
        return [v.option1, v.option2, v.option3].indexOf(optionLabel) !== -1;
      });
      if (match) {
        select.value = String(match.id);
        select.dispatchEvent(new Event('change',  { bubbles: true }));
        select.dispatchEvent(new Event('input',   { bubbles: true }));
        // Also fire Prestige's custom event directly
        document.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: match }
        }));
        return;
      }
    }

    // d) No selector found — at least filter the gallery visually
    filterGallery(keyword);
  }

  // ── Event listeners ────────────────────────────────────────────────────────

  // 1. Prestige native event (fired by theme JS on variant swatch/select change)
  document.addEventListener('variant:changed', function (e) {
    if (e.detail && e.detail.variant) {
      handleVariantChange(e.detail.variant);
    }
  });

  // 2. Recharge / standard select change fallback
  document.addEventListener('change', function (e) {
    const select = e.target.closest('select[name="id"], select[data-variant-select]');
    if (select && select.value) {
      const variant = findVariant(select.value);
      if (variant) handleVariantChange(variant);
    }
  });

  // 3. Recharge widget posts messages on subscription/one-time toggle changes
  // which can cause a variant re-render — re-apply after any Recharge mutation
  if (window.MutationObserver) {
    const observer = new MutationObserver(function () {
      const select = document.querySelector('select[name="id"], select[data-variant-select]');
      if (select && select.value) {
        const variant = findVariant(select.value);
        if (variant) handleVariantChange(variant);
      }
    });
    // Observe Recharge's container for re-renders
    document.addEventListener('DOMContentLoaded', function () {
      const rechargeContainer = document.querySelector('[data-rc-component], .rc_widget, #rc-widget-container');
      if (rechargeContainer) {
        observer.observe(rechargeContainer, { childList: true, subtree: false });
      }
    });
  }

  // ── Initialise on page load ────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(function () {

      // 1. Source routing: if customer arrived from a collection page, pre-select
      //    and show the matching variant gallery before anything else runs.
      const sourceKeyword = getSourceKeyword();
      if (sourceKeyword) {
        autoSelectVariantFromSource();
        return; // autoSelect triggers variant:changed which calls handleVariantChange
      }

      // 2. No source signal — fall back to whatever variant is currently selected
      const select =
        document.querySelector('select[name="id"]') ||
        document.querySelector('select[data-variant-select]');
      if (select && select.value) {
        const variant = findVariant(select.value);
        if (variant) handleVariantChange(variant);
      }

    }, CONFIG.initDelay);
  });

})();
